import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import Modal from "../../../components/Modal"
import Icon from "../../../Icon"
import { IconButton } from "../../Users/userStyles"
import { sendChargerCommand } from "./chargerCommand"
import { CloseButtonContainer, ComingSoonBadge, Content, Layout, SideMenu, SideMenuItem, SideMenuTitle } from "./integrationStyles"
import HttpApiPanel from "./HttpApiPanel"
import ModbusPanel from "./ModbusPanel"

// The apps' Integrations menu (VLT-2695): HTTP API, Modbus, and Alexa / Google as "coming soon" rows,
// as the iOS app shows them. Modbus messages exist from App-Charger protocol v43, so, as in the apps,
// older chargers do not get the row at all.
const MODBUS_MIN_PROTOCOL = 43

interface ProductInfoReply {
  pr_ver?: number
  network_stat?: { wifi?: { ip?: string }, ethernet?: { ip_address?: string } }
}

type Page = 'httpApi' | 'modbus'

export default function IntegrationsModal({ chargerId, onClose }: { chargerId: string, onClose: () => void }) {
  const { t } = useTranslation()
  const [page, setPage] = useState<Page>('httpApi')
  const [productInfo, setProductInfo] = useState<ProductInfoReply | null>()

  useEffect(() => {
    sendChargerCommand<ProductInfoReply>(chargerId, { msg_type: 12 })
      .then(setProductInfo)
      .catch(err => {
        console.log('Unable to read the product info', err)
        setProductInfo(null)
      })
  }, [chargerId])

  const isModbusSupported = (productInfo?.pr_ver ?? 0) >= MODBUS_MIN_PROTOCOL
  // Ethernet before Wi-Fi, as General Information picks it: in AP mode the Wi-Fi address is the
  // charger's own hotspot, which a Modbus TCP master on the LAN cannot reach.
  const ipAddress = productInfo?.network_stat?.ethernet?.ip_address || productInfo?.network_stat?.wifi?.ip || '-'

  const menuItem = (id: Page, labelKey: string) => (
    <SideMenuItem key={id} role="tab" aria-selected={page === id} $selected={page === id} onClick={() => setPage(id)}>
      {t(labelKey)}
    </SideMenuItem>
  )

  return (
    <Modal onEscape={onClose}>
      <Layout role="dialog" aria-modal="true" aria-label={t('charger_integrations')}>
        <SideMenu role="tablist" aria-orientation="vertical">
          <SideMenuTitle>{t('charger_integrations')}</SideMenuTitle>
          {menuItem('httpApi', 'charger_httpApi')}
          {isModbusSupported && menuItem('modbus', 'charger_modbus')}
          <SideMenuItem $selected={false} disabled>
            {t('charger_alexa')}
            <ComingSoonBadge>{t('charger_comingSoon')}</ComingSoonBadge>
          </SideMenuItem>
          <SideMenuItem $selected={false} disabled>
            {t('charger_google')}
            <ComingSoonBadge>{t('charger_comingSoon')}</ComingSoonBadge>
          </SideMenuItem>
        </SideMenu>
        <Content role="tabpanel">
          <CloseButtonContainer>
            <IconButton aria-label={t('charger_close')} onClick={onClose}>
              <Icon path="general.x" width="20px" height="20px" />
            </IconButton>
          </CloseButtonContainer>
          {page === 'httpApi' && <HttpApiPanel chargerId={chargerId} />}
          {page === 'modbus' && isModbusSupported && <ModbusPanel chargerId={chargerId} ipAddress={ipAddress} />}
        </Content>
      </Layout>
    </Modal>
  )
}
